import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { extractErrorMessage } from "@/lib/utils";
// import { useOrganization } from "@clerk/clerk-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusCircle, UserRoundPlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { ScrollArea } from "../ui/scroll-area";

const inviteFormSchema = z.object({
  members: z
    .array(
      z.object({
        email: z
          .string()
          .trim()
          .min(1, "Email is required")
          .email("Please enter a valid email address"),
        roleId: z.string().optional(),
      }),
    )
    .min(1, "At least one member is required"),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

export function InviteUserPopup({
  onInvite,
  roles = [],
  inviteMember,
}: {
  onInvite: () => void;
  roles?: any[];
  inviteMember?: (email: string, roleId?: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm" onClick={() => setOpen(true)}>
          <UserRoundPlus size={16} />
          Invite Users
        </Button>
      </DialogTrigger>
      <DialogContent className="p-0 gap-0">
        <InviteUserFormContent
          setOpen={setOpen}
          onInvite={onInvite}
          roles={roles}
          inviteMember={inviteMember}
        />
      </DialogContent>
    </Dialog>
  );
}

const InviteUserFormContent = ({
  setOpen,
  onInvite,
  roles = [],
  inviteMember,
}: {
  setOpen: (open: boolean) => void;
  onInvite: () => void;
  roles?: any[];
  inviteMember?: (email: string, roleId?: string) => Promise<unknown>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  // const { organization } = useOrganization();

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    mode: "onChange",
    defaultValues: {
      members: [{ email: "", roleId: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "members",
  });

  const handleInvite = async (values: InviteFormValues) => {
    setIsLoading(true);

    try {
      const emails = values.members.map((member) => member.email.toLowerCase());
      const hasDuplicates = new Set(emails).size !== emails.length;
      if (hasDuplicates) {
        toast.error("Duplicate email addresses are not allowed");
        return;
      }

      if (!inviteMember) {
        toast.error("Invite function not available");
        return;
      }

      // Invite each member with their selected role
      const invitePromises = values.members.map((member) =>
        inviteMember(member.email.toLowerCase(), member.roleId || undefined)
      );

      await Promise.all(invitePromises);

      toast.success("Invitations sent successfully");
      onInvite();
      form.reset({
        members: [{ email: "", roleId: "" }],
      });

      window.cioanalytics?.track("team_member_invited", {
        count: emails.length,
        emails: emails,
      });

      setOpen(false);
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to send invitations");
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogHeader className="p-6 pb-0">
        <DialogTitle>Invite Users</DialogTitle>
        <DialogDescription>Invite new members to your team</DialogDescription>
      </DialogHeader>

      <ScrollArea className="sm:max-h-[300px] min-h-[100px] w-full">
        <Form {...form}>
          <form
            className="space-y-4 p-6"
            onSubmit={form.handleSubmit(handleInvite)}
          >
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-start gap-2">
                  <span className="text-sm text-muted-foreground pt-2.5">
                    {(index + 1).toString().padStart(2, "0")}
                  </span>
                  <div className="flex-1 flex items-start gap-2">
                    <FormField
                      control={form.control}
                      name={`members.${index}.email`}
                      render={({ field }) => (
                        <FormItem className="flex-1 space-y-1">
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="user@domain.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`members.${index}.roleId`}
                      render={({ field }) => (
                        <FormItem className="w-[200px] space-y-1">
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || ""}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select role" />
                              </SelectTrigger>
                              <SelectContent>
                                {roles.map((role) => (
                                  <SelectItem key={role.id} value={role.id}>
                                    {role.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {index > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mt-0.5"
                        onClick={() => remove(index)}
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </form>
        </Form>
      </ScrollArea>

      <div className="p-6 pt-0 space-y-4">
        <Button
          type="button"
          variant="outline"
          className="flex items-center gap-2 mt-2 border-dashed w-full shadow-none"
          onClick={() => append({ email: "", roleId: "" })}
        >
          <PlusCircle size={16} />
          Add more
        </Button>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              form.reset({
                members: [{ email: "", roleId: "" }],
              });
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isLoading}
            onClick={() => {
              form.handleSubmit(handleInvite)();
            }}
          >
            {isLoading ? "Sending..." : "Send Invites"}
          </Button>
        </DialogFooter>
      </div>
    </>
  );
};
